<template>
  <section class="scan-table-card">
    <div v-if="isLoading" class="state-block">
      <div v-for="n in 3" :key="n" class="row-skeleton" />
    </div>

    <div v-else-if="!scanRecords.length" class="state-block empty-state">
      <v-icon size="40" color="#4a5a60">mdi-camera-off-outline</v-icon>
      <p class="empty-title">No scans yet</p>
      <p class="empty-subtitle">Add a new scan to register a source capture for this asset.</p>
    </div>

    <template v-else>
      <div v-if="!isCompact" class="table-wrap">
        <table class="scan-table">
          <thead>
            <tr>
              <th class="col-scan">Scan</th>
              <th class="col-type">Type</th>
              <th class="col-status">Status</th>
              <th class="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="scan in scanRecords" :key="scan.id">
              <td class="col-scan">
                <div class="scan-cell">
                  <span class="scan-thumb"><v-icon size="20" color="#7f939d">{{ scanIcon(scan) }}</v-icon></span>
                  <span class="scan-name">{{ scan.name }}</span>
                </div>
              </td>
              <td class="col-type">
                <span class="capture-chip" :class="captureClass(scan)">
                  <v-icon size="14">{{ captureIcon(scan) }}</v-icon>
                  {{ captureText(scan) }}
                </span>
              </td>
              <td class="col-status">
                <ScanStatusPill :status="scan.status" />
              </td>
              <td class="col-actions">
                <v-btn class="row-btn" size="small" variant="text" @click="handleAction(scan)">
                  Details
                  <v-icon size="16" end>mdi-arrow-right</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="card-list">
        <article v-for="scan in scanRecords" :key="scan.id" class="scan-card">
          <header class="scan-card-head">
            <div class="scan-card-title">
              <span class="scan-thumb"><v-icon size="20" color="#7f939d">{{ scanIcon(scan) }}</v-icon></span>
              <div class="scan-card-name">{{ scan.name }}</div>
            </div>
            <ScanStatusPill :status="scan.status" />
          </header>

          <div class="scan-card-meta">
            <span class="capture-chip" :class="captureClass(scan)">
              <v-icon size="14">{{ captureIcon(scan) }}</v-icon>
              {{ captureText(scan) }}
            </span>
          </div>

          <div class="scan-card-actions">
            <v-btn class="row-btn" size="small" variant="text" @click="handleAction(scan)">
              Details
              <v-icon size="16" end>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </article>
      </div>
    </template>
  </section>

  <OverlayWindow :isVisible="isDetailsOverlayVisible" overlayTitle="Scan Details" :centralize-window="true"
    @closeOverlay="isDetailsOverlayVisible = false">
    <ScanDetailsDialog @OnRefresh="refreshTable" :scan="selectedScan" @onCloseWindow="closeWindow"
      @onGenerationStarted="generationStarted" />
  </OverlayWindow>
</template>

<script>
import { useDisplay } from 'vuetify';
import ModelVersionService from '@/services/ModelVersionService';
import ScanStatusPill from '../pills/ScanStatusPill.vue';
import OverlayWindow from '../OverlayWindow.vue';
import ScanDetailsDialog from '../dialog/ScanDetailsDialog.vue';

export default {
  name: "ScanTable",
  components: { ScanStatusPill, OverlayWindow, ScanDetailsDialog },
  props: {
    assetId: { type: String, default: "" }
  },
  emits: ['onGenerationStarted'],
  setup() {
    const { smAndDown } = useDisplay();
    return { isCompact: smAndDown };
  },
  data() {
    return {
      isLoading: true,
      scanRecords: [],
      isDetailsOverlayVisible: false,
      selectedScan: null,
    }
  },
  async mounted() {
    await this.getVersionByAssetId(this.assetId);
  },
  methods: {
    async closeWindow() {
      this.isDetailsOverlayVisible = false;
      await this.refreshTable();
    },
    async refreshTable() {
      this.isLoading = true;
      await this.getVersionByAssetId(this.assetId);
    },
    async getVersionByAssetId(assetId) {
      try {
        this.scanRecords = await ModelVersionService.getScansByAssetId(assetId);
      } finally {
        this.isLoading = false;
      }
    },
    handleAction(scan) {
      this.selectedScan = scan;
      this.isDetailsOverlayVisible = true;
    },
    // Capture-type chip. Anything without an explicit method defaults to Walkthrough.
    captureText(scan) {
      return scan?.captureMethod || 'Walkthrough';
    },
    captureIcon(scan) {
      return scan?.captureMethod === 'Drone' ? 'mdi-quadcopter' : 'mdi-walk';
    },
    captureClass(scan) {
      return scan?.captureMethod === 'Drone' ? 'is-drone' : 'is-walk';
    },
    // Icon for the scan's leading tile (no thumbnails on scan records).
    scanIcon(scan) {
      return scan?.captureMethod === 'Drone' ? 'mdi-quadcopter' : 'mdi-video-outline';
    },
    generationStarted() {
      this.$emit('onGenerationStarted');
    }
  }
}
</script>

<style scoped>
.scan-table-card {
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  overflow: hidden;
}

/* ----- Loading / empty ----- */
.state-block {
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.row-skeleton {
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(90deg,
    rgba(255, 255, 255, 0.02) 0%,
    rgba(255, 255, 255, 0.05) 50%,
    rgba(255, 255, 255, 0.02) 100%);
  background-size: 200% 100%;
  animation: skel-shimmer 1.4s ease-in-out infinite;
}

@keyframes skel-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.empty-state {
  align-items: center;
  text-align: center;
  padding: 40px 16px;
  color: #8aa0aa;
}

.empty-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #cfd8dc;
  margin: 12px 0 4px;
}

.empty-subtitle {
  font-size: 0.88rem;
  margin: 0;
  max-width: 380px;
}

/* ----- Desktop table ----- */
.table-wrap {
  overflow-x: auto;
}

.scan-table {
  width: 100%;
  border-collapse: collapse;
  color: #cfd8dc;
}

.scan-table thead th {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8aa0aa;
  font-weight: 600;
  text-align: left;
  padding: 14px 16px;
  background: rgba(0, 0, 0, 0.18);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.scan-table tbody tr {
  transition: background 0.15s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.scan-table tbody tr:last-child {
  border-bottom: none;
}

.scan-table tbody tr:hover {
  background: rgba(100, 255, 218, 0.04);
}

.scan-table td {
  padding: 14px 16px;
  font-size: 0.92rem;
  vertical-align: middle;
}

.col-scan {
  min-width: 220px;
}

.scan-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.scan-thumb {
  flex: 0 0 auto;
  width: 44px;
  height: 34px;
  border-radius: 8px;
  background: #1c272c;
  border: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}

.scan-name {
  color: #ffffff;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-type {
  width: 150px;
  white-space: nowrap;
}

.col-status {
  width: 160px;
  white-space: nowrap;
}

/* ----- Capture-type chip ----- */
.capture-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #cfd8dc;
}

.capture-chip.is-walk {
  color: var(--duallo-accent);
  background: rgba(100, 255, 218, 0.1);
  border-color: rgba(100, 255, 218, 0.28);
}

.capture-chip.is-drone {
  color: #82b1ff;
  background: rgba(130, 177, 255, 0.1);
  border-color: rgba(130, 177, 255, 0.3);
}

.col-actions {
  width: 110px;
  text-align: right;
  white-space: nowrap;
}

.scan-table thead .col-actions {
  text-align: right;
}

/* ----- Shared action button ----- */
.row-btn {
  color: var(--duallo-accent) !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.02em;
  border-radius: 8px !important;
}

.row-btn:hover {
  background: rgba(100, 255, 218, 0.08) !important;
}

/* ----- Mobile card list ----- */
.card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
}

.scan-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

.scan-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.scan-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
}

.scan-card-name {
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.3;
  word-break: break-word;
  min-width: 0;
}

.scan-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.scan-card-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
